import { API_BASE, getAuthHeaders } from '../utils/supabase/client';

export interface HuddleStatus {
  configured: boolean;
  active: boolean;
  participantCount: number;
  maxParticipants: number;
}

export interface HuddleCredentials {
  serverUrl: string;
  token: string;
  roomName: string;
}

export type HuddleErrorCode = 'server_unavailable' | 'setup_required' | 'forbidden' | 'unauthorized' | 'room_full' | 'unavailable' | 'invalid_response' | 'call_active' | 'native_unavailable';

const ERROR_MESSAGES: Record<HuddleErrorCode, string> = {
  server_unavailable: 'Huddles are not available on this server yet.',
  setup_required: 'Huddles need to be enabled for this workspace.',
  forbidden: 'You do not have access to this conversation’s huddle.',
  unauthorized: 'Sign in again to join this huddle.',
  room_full: 'This huddle is full. Try again when someone leaves.',
  unavailable: 'Could not reach the huddle service. Check your connection and try again.',
  invalid_response: 'The huddle service returned an incomplete response. Try again.',
  call_active: 'Finish your current call before joining a huddle.',
  native_unavailable: 'Huddles are available in the browser. Native meeting support is not enabled yet.',
};

export class HuddleError extends Error {
  constructor(public readonly code: HuddleErrorCode) {
    super(ERROR_MESSAGES[code]);
    this.name = 'HuddleError';
  }
}

function abortable<T>(pending: Promise<T>, signal: AbortSignal): Promise<T> {
  signal.throwIfAborted();
  return new Promise((resolve, reject) => {
    const cancel = () => { signal.removeEventListener('abort', cancel); reject(new DOMException('Huddle request cancelled', 'AbortError')); };
    signal.addEventListener('abort', cancel, { once: true });
    pending.then((value) => { signal.removeEventListener('abort', cancel); resolve(value); },
      (error: unknown) => { signal.removeEventListener('abort', cancel); reject(error); });
  });
}

async function request(chatId: string, join: boolean, signal?: AbortSignal): Promise<unknown> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timeout = setTimeout(abort, 15_000);
  try {
    controller.signal.throwIfAborted();
    const headers = await abortable(getAuthHeaders({ 'Content-Type': 'application/json' }), controller.signal);
    controller.signal.throwIfAborted();
    const response = await fetch(`${API_BASE}/huddles/${encodeURIComponent(chatId)}${join ? '/join' : ''}`, {
      method: join ? 'POST' : 'GET', headers, signal: controller.signal, cache: 'no-store',
    });
    if (!response.ok) {
      let code: unknown;
      try { code = (await response.json()).code; } catch { /* A missing route may return HTML. */ }
      if (response.status === 404) throw new HuddleError('server_unavailable');
      if (response.status === 401) throw new HuddleError('unauthorized');
      if (response.status === 403) throw new HuddleError('forbidden');
      if (response.status === 409 && code === 'room_full') throw new HuddleError('room_full');
      if (code === 'setup_required') throw new HuddleError('setup_required');
      throw new HuddleError('unavailable');
    }
    try { return await response.json(); } catch { throw new HuddleError('invalid_response'); }
  } catch (error) {
    if (signal?.aborted) throw new DOMException('Huddle request cancelled', 'AbortError');
    if (error instanceof HuddleError) throw error;
    throw new HuddleError('unavailable');
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export const HuddleService = {
  async getStatus(chatId: string, signal?: AbortSignal): Promise<HuddleStatus> {
    const result = await request(chatId, false, signal);
    if (!isRecord(result) || typeof result.configured !== 'boolean' || typeof result.active !== 'boolean'
      || !Number.isInteger(result.participantCount) || Number(result.participantCount) < 0
      || !Number.isInteger(result.maxParticipants) || Number(result.maxParticipants) < 1) {
      throw new HuddleError('invalid_response');
    }
    return result as unknown as HuddleStatus;
  },
  async join(chatId: string, signal?: AbortSignal): Promise<HuddleCredentials> {
    const result = await request(chatId, true, signal);
    if (!isRecord(result) || typeof result.serverUrl !== 'string' || typeof result.token !== 'string'
      || !result.token.trim() || typeof result.roomName !== 'string' || !result.roomName.trim()) {
      throw new HuddleError('invalid_response');
    }
    let url: URL;
    try { url = new URL(result.serverUrl); } catch { throw new HuddleError('invalid_response'); }
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    if (url.username || url.password || url.search || url.hash || (url.pathname !== '/' && url.pathname !== '')
      || (!['wss:', 'https:'].includes(url.protocol) && !(local && ['ws:', 'http:'].includes(url.protocol)))) {
      throw new HuddleError('invalid_response');
    }
    return result as unknown as HuddleCredentials;
  },
};
